import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCompanyDetails } from '../../utilities/api';

export const fetchCompanyData = createAsyncThunk('company/fetch', async ({ rolename, clientname }, { rejectWithValue }) => {
    try {
        const data = await getCompanyDetails({ rolename, clientname });
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch company data');
    }
});

const initialState = {
    clients: [],
    allBranchs: [],
    companies: [],
    locations: [],
    allPlatform: [],
    loading: true,
    error: null,
    selectedClient: null,
    selectedBranch: null,
    selectedPlatform: null,
    statusFilter: 'all'
};

const companySlice = createSlice({
    name: 'company',
    initialState,
    reducers: {
        setSelectedClient: (state, action) => {
            state.selectedClient = action.payload;
            state.selectedBranch = null;
            state.selectedPlatform = null;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
            state.selectedPlatform = null;
        },
        setSelectedPlatform: (state, action) => {
            state.selectedPlatform = action.payload;
        },
        setStatusFilter: (state, action) => {
            state.statusFilter = action.payload;
        },
        resetFilters: (state) => {
            state.selectedClient = null;
            state.selectedBranch = null;
            state.selectedPlatform = null;
            state.statusFilter = 'all';
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCompanyData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCompanyData.fulfilled, (state, action) => {
                state.loading = false;
                const raw = action.payload || {};
                state.clients = Array.isArray(raw.clients) ? raw.clients : [];
                state.allBranchs = Array.isArray(raw.branchs) ? raw.branchs : [];
                state.companies = Array.isArray(raw.company) ? raw.company : [];
                state.locations = Array.isArray(raw.locations) ? raw.locations : [];
                state.allPlatform = Array.isArray(raw.platform) ? raw.platform : [];
            })
            .addCase(fetchCompanyData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { setSelectedClient, setSelectedBranch, setSelectedPlatform, setStatusFilter, resetFilters } = companySlice.actions;

export const selectClients = (state) => state.company.clients;
export const selectAllBranchs = (state) => state.company.allBranchs;
export const selectCompanies = (state) => state.company.companies;
export const selectLocations = (state) => state.company.locations;
export const selectAllPlatform = (state) => state.company.allPlatform;
export const selectLoading = (state) => state.company.loading;
export const selectError = (state) => state.company.error;
export const selectSelectedClient = (state) => state.company.selectedClient;
export const selectSelectedBranch = (state) => state.company.selectedBranch;
export const selectSelectedPlatform = (state) => state.company.selectedPlatform;
export const selectStatusFilter = (state) => state.company.statusFilter;

export const selectFilteredBranches = (state) => {
    const { allBranchs, selectedClient } = state.company;
    if (!selectedClient) return [];
    return allBranchs.filter((b) => String(b.client_id) === String(selectedClient.client_id));
};

export const selectFilteredPlatforms = (state) => {
    const { companies, allPlatform, selectedClient, selectedBranch } = state.company;
    let source = companies;
    if (selectedBranch) {
        source = companies.filter((c) => String(c.branch_id) === String(selectedBranch.branch_id));
    } else if (selectedClient) {
        source = companies.filter((c) => String(c.client_id) === String(selectedClient.client_id));
    }
    const availableIds = [...new Set(source.map((c) => String(c.platform_id)))];
    const relevant = allPlatform.filter((p) => availableIds.includes(String(p.platform_id)));
    return relevant.length > 0 ? relevant : allPlatform;
};

export const selectFilteredCompanies = (state) => {
    const { companies, selectedClient, selectedBranch, selectedPlatform, statusFilter } = state.company;
    let result = [...companies];
    if (selectedClient) result = result.filter((c) => String(c.client_id) === String(selectedClient.client_id));
    if (selectedBranch) result = result.filter((c) => String(c.branch_id) === String(selectedBranch.branch_id));
    if (selectedPlatform) result = result.filter((c) => String(c.platform_id) === String(selectedPlatform.platform_id));
    if (statusFilter === 'active') result = result.filter((c) => c.compmay_status === 'Active');
    else if (statusFilter === 'inactive') result = result.filter((c) => c.compmay_status === 'Inactive');
    return result;
};

export default companySlice.reducer;
